import { ReactNode } from "react";
import { Auth0Provider as Auth0ProviderBase } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { auth0Config } from "@/config/auth0";
import { validateAndSanitizeReturnTo } from "@/utils/urlValidation";

/**
 * Auth0Provider Wrapper Component
 *
 * Wraps the application with Auth0 authentication provider.
 * Handles redirect callbacks after authentication.
 *
 * This component needs to be inside BrowserRouter to use useNavigate.
 */
interface Auth0ProviderProps {
  children: ReactNode;
}

function Auth0ProviderInner({ children }: Auth0ProviderProps) {
  // This inner component can be used to initialize API client later
  return <>{children}</>;
}

export function Auth0ProviderWithCallback({ children }: Auth0ProviderProps) {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    // Validate returnTo to prevent open redirect vulnerabilities
    const safeReturnTo = validateAndSanitizeReturnTo(appState?.returnTo);
    navigate(safeReturnTo, { replace: true });
  };

  return (
    <Auth0ProviderBase
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={auth0Config.authorizationParams}
      useRefreshTokens={auth0Config.useRefreshTokens}
      cacheLocation={auth0Config.cacheLocation}
      onRedirectCallback={onRedirectCallback}
    >
      <Auth0ProviderInner>{children}</Auth0ProviderInner>
    </Auth0ProviderBase>
  );
}
